<template>
  <div class="landing">
    <header class="landing__header">
      <div class="landing__logo">
        <svg class="logo-icon" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect width="32" height="32" rx="8" fill="#1e293b"/>
          <path d="M8 10h10M8 16h7M8 22h10" stroke="white" stroke-width="2.2" stroke-linecap="round"/>
          <circle cx="23" cy="22" r="4" fill="white" fill-opacity="0.2" stroke="white" stroke-width="1.5"/>
          <path d="M21.5 22l1 1 2-2" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <span class="logo-text">FormIQ</span>
      </div>
    </header>

    <main class="landing__main">
      <div class="landing__hero">
        <div class="hero__badge">Interaktive Demo</div>
        <h1 class="hero__title">
          Intelligentere Anträge,<br />
          <span class="hero__accent">unterstützt durch KI</span>
        </h1>
        <p class="hero__subtitle">
          Erleben Sie, wie FormIQ komplexe Behördenanträge in geführte,
          intelligente Prozesse verwandelt – mit smartem Dokument-Upload
          und Echtzeit-Chat-Assistenz.
        </p>
        <div class="hero__disclaimer">
          ⚠️ &nbsp;<strong>Demo-Hinweis:</strong> Dies ist eine Showcase-Umgebung.
          Es werden keine echten Antragsdaten verarbeitet oder gespeichert.
        </div>
        <button class="hero__cta" @click="goToLogin">
          Zum Test-Login
          <span class="cta__arrow">→</span>
        </button>
      </div>

      <div class="landing__modules">
        <div class="module-card">
          <div class="module-card__icon module-card__icon--blue">💬</div>
          <h2 class="module-card__title">FormIQ 1<br /><span>Chat Assist</span></h2>
          <p class="module-card__desc">
            Kontextsensitive Chat-Führung durch jeden Schritt des Antrags.
            Stellen Sie Fragen, navigieren Sie durch Abschnitte und erhalten
            Sie sofortige Antworten – ohne das Formular zu verlassen.
          </p>
          <ul class="module-card__features">
            <li>Verschiebbares &amp; skalierbares Chat-Fenster</li>
            <li>Mehrsprachig (DE / EN / TR)</li>
            <li>Kontextbewusste Navigation</li>
          </ul>
        </div>

        <div class="module-card">
          <div class="module-card__icon module-card__icon--green">📄</div>
          <h2 class="module-card__title">FormIQ 2<br /><span>AutoFill</span></h2>
          <p class="module-card__desc">
            Laden Sie eine Vollmacht oder ein Zertifikat hoch und beobachten
            Sie, wie FormIQ relevante Formularfelder automatisch erkennt und
            befüllt – zeitsparend und fehlerreduzierend.
          </p>
          <ul class="module-card__features">
            <li>PDF, DOCX, PNG, JPG Upload</li>
            <li>Automatisch hervorgehobene Felder</li>
            <li>Alle Felder weiterhin bearbeitbar</li>
          </ul>
        </div>
      </div>

      <div class="landing__applications">
        <h3 class="applications__heading">Zwei Musteranträge</h3>
        <div class="applications__list">
          <div class="app-pill app-pill--a">
            <span class="app-pill__icon">📚</span>
            Förderantrag für deutschsprachige Literatur
          </div>
          <div class="app-pill app-pill--b">
            <span class="app-pill__icon">🏛️</span>
            Einbürgerungsantrag – deutsche Staatsangehörigkeit
          </div>
        </div>
      </div>
    </main>

    <footer class="landing__footer">
      <p>© 2025 FormIQ Demo · Es werden keine echten Daten erfasst oder gespeichert</p>
    </footer>
  </div>
</template>

<script>
import { useRouter } from 'vue-router';

export default {
  name: 'LandingPage',
  setup() {
    const router = useRouter();
    const goToLogin = () => router.push('/login');
    return { goToLogin };
  },
};
</script>

<style scoped>
.landing {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f8fafc;
  color: #0f172a;
}

/* ---- Header ---- */
.landing__header {
  padding: 20px 48px;
  display: flex;
  align-items: center;
  background: #ffffff;
  border-bottom: 3px solid #c0121c;
}

.landing__logo {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 20px;
  font-weight: 800;
  letter-spacing: 0.5px;
}

.logo-icon { width: 32px; height: 32px; flex-shrink: 0; }
.logo-text { color: #0f172a; }

/* ---- Main ---- */
.landing__main {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 64px 24px 80px;
  gap: 72px;
}

/* ---- Hero ---- */
.landing__hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 680px;
  gap: 22px;
}

.hero__badge {
  background: #f1f5f9;
  border: 1px solid #cbd5e1;
  color: #475569;
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
}

.hero__title {
  font-size: clamp(30px, 5vw, 50px);
  font-weight: 800;
  line-height: 1.15;
  color: #0f172a;
}

.hero__accent { color: #334155; }

.hero__subtitle {
  font-size: 17px;
  color: #475569;
  line-height: 1.7;
  max-width: 580px;
}

.hero__disclaimer {
  background: #fffbeb;
  border: 1px solid #fde68a;
  color: #92400e;
  padding: 12px 20px;
  border-radius: 10px;
  font-size: 14px;
  line-height: 1.5;
  width: 100%;
}

.hero__cta {
  margin-top: 4px;
  background: #1e293b;
  color: #fff;
  border: none;
  padding: 16px 40px;
  font-size: 16px;
  font-weight: 700;
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  transition: background 0.2s, transform 0.15s, box-shadow 0.15s;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.18);
}
.hero__cta:hover {
  background: #0f172a;
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.28);
}
.cta__arrow { font-size: 18px; }

/* ---- Module cards ---- */
.landing__modules {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  justify-content: center;
  width: 100%;
  max-width: 900px;
}

.module-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 32px 28px;
  flex: 1;
  min-width: 280px;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  box-shadow: 0 1px 8px rgba(15, 23, 42, 0.06);
  transition: box-shadow 0.2s, transform 0.2s;
}
.module-card:hover {
  box-shadow: 0 6px 24px rgba(15, 23, 42, 0.1);
  transform: translateY(-2px);
}

.module-card__icon {
  font-size: 32px;
  width: 60px;
  height: 60px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.module-card__icon--blue { background: #eff6ff; }
.module-card__icon--green { background: #f0fdf4; }

.module-card__title {
  font-size: 19px;
  font-weight: 800;
  color: #0f172a;
  line-height: 1.3;
}
.module-card__title span {
  color: #475569;
  font-size: 15px;
  font-weight: 600;
}

.module-card__desc {
  font-size: 14px;
  color: #64748b;
  line-height: 1.65;
}

.module-card__features {
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 4px;
}
.module-card__features li {
  font-size: 13px;
  color: #374151;
  padding-left: 20px;
  position: relative;
}
.module-card__features li::before {
  content: '✓';
  position: absolute;
  left: 0;
  color: #10b981;
  font-weight: 700;
}

/* ---- Applications pills ---- */
.landing__applications {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}
.applications__heading {
  font-size: 13px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 2px;
}
.applications__list {
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
  justify-content: center;
}

.app-pill {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 22px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  color: #1e293b;
  box-shadow: 0 1px 4px rgba(15,23,42,0.06);
}
.app-pill--a { border-left: 3px solid #3b82f6; }
.app-pill--b { border-left: 3px solid #10b981; }
.app-pill__icon { font-size: 18px; }

/* ---- Footer ---- */
.landing__footer {
  text-align: center;
  padding: 20px;
  font-size: 13px;
  color: #94a3b8;
  border-top: 1px solid #e2e8f0;
  background: #ffffff;
}
</style>
